import cap from './cap';
import isOverflown from './is-overFlown';

// corresponding to css breakpoints
const breakpoints = {
    xs: 320,
    sm: 576,
    md: 768,
    lg: 992,
    xl: 1200,
    xxl: 1440,
};

/**
 * Check the current screen matches which breakpoint
 * @param {Object} param0
 */
function breakAt({ callback, from, to }) {
    const fromPoint = breakpoints[from] || null;
    const toPoint = breakpoints[to] - 1 || null;

    const fromMatch = fromPoint ? `(min-width: ${fromPoint}px)` : null;
    const toMatch = toPoint ? `(max-width: ${toPoint}px)` : null;

    let match = null;
    if (fromMatch && toMatch) {
        match = `${fromMatch} and ${toMatch}`;
    } else if (fromMatch || toMatch) {
        match = fromMatch || toMatch;
    } else {
        return;
    }

    if (match && window.matchMedia(match).matches) {
        callback();
    }
}

// https://css-tricks.com/linearly-scale-font-size-with-css-clamp-based-on-the-viewport/
function clampBuilder(minWidthPx, maxWidthPx, minFontSizePx, maxFontSizePx, windowW) {
    const slope = (maxFontSizePx - minFontSizePx) / (maxWidthPx - minWidthPx);
    const yAxisIntersection = -minWidthPx * slope + minFontSizePx;
    const fontSize = yAxisIntersection + slope * windowW;

    return cap(fontSize, minFontSizePx, maxFontSizePx);
}

/**
 * Scale font size from breakpoint to breakpoint
 * @param {*} el
 * @param {*} minWidthPx
 * @param {*} maxWidthPx
 * @param {*} minFontSizePx
 * @param {*} maxFontSizePx
 * @param {*} containerWidthPx
 */
function fontScale(
    el,
    minWidthPx,
    maxWidthPx,
    minFontSizePx,
    maxFontSizePx,
    containerWidthPx = window.innerWidth
) {
    el.style.fontSize = `${clampBuilder(
        minWidthPx,
        maxWidthPx,
        minFontSizePx,
        maxFontSizePx,
        containerWidthPx
    )}px`;
}

/**
 * Scale everything of an element when its parent container shinks
 * @param {*} el
 * @param {*} containerWidthPx
 * @param {*} relativeWidthPx
 * @param {*} transformOrigin
 * @param {*} minScale
 * @param {*} maxScale
 */
function transformScale(
    el,
    containerWidthPx = window.innerWidth,
    relativeWidthPx,
    transformOrigin = 'center left',
    minScale = 0.3,
    maxScale = 1
) {
    const scale = cap(containerWidthPx / relativeWidthPx, minScale, maxScale);

    el.style.transform = `scale(${scale})`;
    if (transformOrigin) {
        el.style.transformOrigin = transformOrigin;
    }
}

/**
 * Scale nowrap text that overflows its parent container width
 *
 * @param {*} el
 * @param {*} transformOrigin
 * @param {*} minScale
 * @param {*} maxScale
 */
function overflownTextScale(el, transformOrigin = 'center left', minScale = 0.3, maxScale = 1) {
    el.style.maxWidth = '100%';
    if (isOverflown(el)) {
        const scale = cap(el.clientWidth / el.scrollWidth, minScale, maxScale);

        el.style.transform = `scale(${scale})`;
        if (transformOrigin) {
            el.style.transformOrigin = transformOrigin;
        }
        el.style.removeProperty('max-width');
        return scale;
    }
    el.style.removeProperty('max-width');
    return null;
}

export default {
    breakpoints,
    breakAt,
    fontScale,
    transformScale,
    overflownTextScale,
};
